import React from "react";

import { Link } from "react-router-dom";
import classNames from "classnames";

import { SortDirection } from "types/types";

import { formatMs, formatPercent } from "utils/format";

import styles from "./style.module.scss";
import { useRoutes } from "utils/routes";
import LazyLoader from "components/LazyLoader";
import Grid from "components/Grid";
import { GridColumn, SortOrder } from "components/Grid/util";

interface QueryType {
  id: string;
  databaseId?: string;
  server?: {
    id: string;
    humanId: string;
    name: string;
  };
  serverName?: string;
  postgresRole?: null | {
    id: string;
    name: string;
  };
  postgresRoleName?: string;
  postgresRoleCount: number;
  truncatedQuery: string;
  normalizedQuery: string;
  avgTime: number;
  avgTimeDelta?: number;
  callsPerMinute: number;
  callsPerMinuteDelta?: number;
  bufferHitRatio?: number | null;
  pctOfTotal: number;
  pctOfTotalIo?: number | null;
}

export type QueryStatsTableSortBy = keyof Omit<QueryType, "postgresRole">;

interface SortOptsType {
  sortBy: QueryStatsTableSortBy;
  sortDirection: SortDirection;
}

type BaseProps = {
  queries: QueryType[];
  searchTerm: null | string;
  compare: boolean;
  showingTable?: boolean;
  setSortOpts: (opts: SortOptsType) => void;
  sortOpts: SortOptsType;
  fetchMoreData: (loadedCount: number) => Promise<boolean>;
};

type Props = BaseProps &
  (
    | { showServer: true; databaseId?: undefined; serverId?: undefined }
    | { showServer?: false; databaseId: string; serverId: string }
  );

// Need to turn this into a data loading for the query statistics, similar to
// SchemaTableList - however this is more complicated because this table is
// used twice, once for the query overview, and once for the per-table queries
const QueryStatsTable: React.FunctionComponent<Props> = ({
  databaseId,
  serverId,
  compare,
  queries,
  showingTable,
  showServer,
  sortOpts,
  setSortOpts,
  fetchMoreData,
}) => {
  const { databaseQuery, serverRole, server: serverRoute } = useRoutes();

  const queriesWithRoles: Array<QueryType> = queries.map(
    (q: QueryType): QueryType => {
      return {
        postgresRoleName: q.postgresRole?.name ?? null,
        serverName: q.server?.name ?? null,
        ...q,
      };
    },
  );

  function handleSort(sortBy: QueryStatsTableSortBy, sortOrder: SortOrder) {
    setSortOpts({
      sortBy,
      sortDirection: sortOrder,
    });
  }

  const sortedOrder = sortOpts.sortDirection;
  const sortedBy = sortOpts.sortBy as keyof QueryType;
  const queryColWidth = compare ? "1fr" : "4fr";
  const numColWidth = compare ? "10%" : "12%";
  const columns: GridColumn<QueryType, keyof QueryType>[] = [
    {
      field: "normalizedQuery",
      header: "Query",
      width: queryColWidth,
      style: "query",
      disableSort: true,
      renderer: function QueryCell({ fieldData, rowData }) {
        const href = databaseQuery(
          rowData.databaseId ?? databaseId,
          rowData.id,
        );
        return (
          <Link to={href} title={fieldData}>
            {rowData.truncatedQuery}
          </Link>
        );
      },
    },
    {
      field: "postgresRoleName",
      header: "Role",
      width: showServer ? "minmax(10%,160px)" : "minmax(10%,120px)",
      nullValue: "n/a",
      disableSort: true,
      renderer: function RoleCell({ fieldData, rowData }) {
        const roleServerId = rowData.server?.humanId ?? serverId;
        return (
          <>
            <Link to={serverRole(roleServerId, rowData.postgresRole.id)}>
              {fieldData}
            </Link>{" "}
            {rowData.postgresRoleCount > 1 && (
              <span>+{rowData.postgresRoleCount - 1}</span>
            )}
          </>
        );
      },
    },
    {
      field: "serverName",
      header: "Server",
      width: showServer ? "minmax(10%,120px)" : false,
      nullValue: "n/a",
      disableSort: true,
      renderer: function ServerCell({ fieldData, rowData }) {
        if (!rowData.server) {
          return <>{fieldData}</>;
        }
        return (
          <Link to={serverRoute(rowData.server.humanId)}>{fieldData}</Link>
        );
      },
    },
    {
      field: "avgTime",
      header: "Avg Time (ms)",
      width: numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function AvgTimeCell({ fieldData }) {
        return formatMs(fieldData);
      },
    },
    {
      field: "avgTimeDelta",
      header: "7 Day Trend",
      width: compare ? "10%" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: AvgTimeTrendCell,
    },
    {
      field: "callsPerMinute",
      header: "Calls / min",
      width: numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function CallsPerMinCell({ fieldData }) {
        return fieldData.toFixed(2);
      },
    },
    {
      field: "callsPerMinuteDelta",
      header: "7 Day Trend",
      width: compare ? "10%" : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: CallsTrendCell,
    },
    {
      field: "pctOfTotalIo",
      header: "% of All I/O",
      width: !showingTable ? numColWidth : false,
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function PctOfTotalIoCell({ fieldData }) {
        return formatPercent(fieldData / 100.0);
      },
    },
    {
      field: "pctOfTotal",
      header: "% of All Runtime",
      width: numColWidth,
      style: "number",
      defaultSortOrder: "DESC",
      renderer: function PctOfTotalCell({ fieldData }) {
        return formatPercent(fieldData / 100.0);
      },
    },
  ];

  return (
    <LazyLoader loadedCount={queriesWithRoles.length} loadMore={fetchMoreData}>
      <Grid
        striped
        data={queriesWithRoles}
        columns={columns}
        handleSort={handleSort}
        sortedBy={sortedBy}
        sortedOrder={sortedOrder}
        noRowsText="No queries matched"
      />
    </LazyLoader>
  );
};

function AvgTimeTrendCell({
  fieldData,
  rowData,
}: {
  fieldData: number;
  rowData: { avgTime: number };
}) {
  const delta = fieldData;
  const { avgTime: value } = rowData;

  const prevValue = value - delta;
  const pctDelta = (value - prevValue) / prevValue;
  if (delta === 0) {
    return "(no change)";
  }
  const sign = delta > 0 ? "+" : "";
  const pctChange = sign + formatPercent(pctDelta);
  const absChange = sign + formatMs(delta);
  return (
    <span
      title={pctChange}
      className={classNames(
        delta < 0 && styles.deltaDown,
        delta > 0 && styles.deltaUp,
      )}
    >
      {absChange}
    </span>
  );
}

function CallsTrendCell({
  rowData,
  fieldData,
}: {
  rowData: {
    callsPerMinute: number;
  };
  fieldData: number;
}) {
  const { callsPerMinute } = rowData;

  const delta = fieldData;
  if (delta === 0) {
    return "(no change)";
  }

  const value = callsPerMinute;

  const prevValue = value - delta;
  const pctDelta = (value - prevValue) / prevValue;

  // We only need to add the positive sign; the negative sign is part of the
  // formatted number
  const sign = delta > 0 ? "+" : "";
  const pctChange = sign + formatPercent(pctDelta);
  const absChange = sign + delta.toFixed(2);
  return (
    <span
      title={pctChange}
      className={classNames(
        delta < 0 && styles.deltaDown,
        delta > 0 && styles.deltaUp,
      )}
    >
      {absChange}
    </span>
  );
}

export default QueryStatsTable;
